import { Box, Stack, Text } from "@chakra-ui/react";
import {
  DateTimePicker,
  DateTimePickerProps,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import utcPlugin from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

type KYDDateTimePickerProps = {
  timezone: string;
  label: string;
  name: string;
  required?: boolean;
  isDisabled?: boolean;
  dateTimeProps?: DateTimePickerProps<Dayjs>;
  info?: string;
  value: string;
  onChange: (isoString: string) => void;
  error?: string;
};

const KYDDateTimePicker = ({
  timezone,
  value,
  label,
  required = false,
  isDisabled = false,
  dateTimeProps = {},
  onChange,
  error,
  info = "",
}: KYDDateTimePickerProps) => {
  const isInfo = !!info;
  const isError = !!error;
  const color = isError ? "red.400" : isInfo ? "#A1700D" : "transparent";

  const parsedDate: Dayjs | null = (() => {
    if (!value) {
      return null;
    }
    const d = dayjs(value);
    return d.isValid() ? d : null;
  })();

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack w="full" spacing={0}>
        <DateTimePicker
          label={label}
          value={parsedDate}
          slotProps={{
            textField: {
              error: isError,
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: isInfo ? `${color} !important` : "#E6E6E6",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: isInfo ? `${color} !important` : "#E6E6E6",
                },
                boxShadow: "0px 0px 10px 0px #0000000D",
                "& .MuiInputBase-input": {
                  fontSize: "16px",
                  fontFamily: "Work Sans",
                },
              },
            },
            actionBar: {
              actions: required ? ["accept"] : ["clear", "accept"],
            },
          }}
          closeOnSelect
          disabled={isDisabled}
          onChange={(newDate: Dayjs | null) => {
            if (newDate === null) {
              onChange("");
            } else if (newDate.isValid()) {
              newDate.set("seconds", 0);
              newDate.set("milliseconds", 0);
              onChange(newDate.toISOString());
            }
          }}
          timezone={timezone}
          {...dateTimeProps}
        />

        {isInfo && (
          <Box
            h={4}
            borderLeftWidth="2px"
            borderLeftColor={color}
            pl={2}
            mt={1}
            flex={1}
            display="flex"
            alignItems="center"
          >
            <Text fontSize="sm" color={color}>
              {info}
            </Text>
          </Box>
        )}
        {isError && (
          <Box
            h={4}
            borderLeftWidth="2px"
            borderLeftColor={color}
            pl={2}
            mt={1}
            flex={1}
            display="flex"
            alignItems="center"
          >
            <Text fontSize="sm" color={color}>
              {error}
            </Text>
          </Box>
        )}
      </Stack>
    </LocalizationProvider>
  );
};

export default KYDDateTimePicker;
